/**
 * Hand-written API bindings for the internal customer support feature
 * (docs/prd/20260906_internal-customer-support.md — "UI/API contract (v1)").
 *
 * NOTE on why this does not reuse `apiClient` from "@/api/client":
 * That shared client special-cases `201`/`204`/`PATCH` responses by always
 * returning `data: null` (see `apps/web/src/api/client.ts`). That matches
 * every *existing* generated endpoint in this app (all of them return empty
 * bodies for those cases — verified via `src/api/__generated__/api.schema.ts`),
 * but our contract's `POST /support/tickets` must return a JSON body
 * (`{id,reference,status,createdAt}`) so the public form can show a ticket
 * reference. Reusing `apiClient` as-is would silently discard that body.
 * `apiClient` is out of scope for this task to modify, so this module ships
 * a small local fetch wrapper that follows the same auth/base-url
 * conventions (Supabase bearer token, dev proxy vs. prod API base) but always
 * parses the actual response body.
 *
 * Path convention: every other endpoint in this repo is served through Nest's
 * URI versioning (`app.enableVersioning({ prefix: "api/v", defaultVersion: "1" })`,
 * see `apps/server/src/main.ts`), so paths below are prefixed with `/api/v1`
 * to match, even though the PRD writes them without the prefix.
 *
 * Attachments: `GET /admin/support/attachments/:id` (staff-auth, streams the
 * raw bytes with a real `Content-Disposition` header) and `SupportAttachment`'s
 * shape below are both taken directly from the now-live
 * `apps/server/src/modules/support/support.controller.ts` /
 * `support.types.ts` (re-read at this pass, not assumed): the real shape is
 * `{id,filename,contentType,size,available,gmailAttachmentId?}` — a boolean
 * `available` flag, NOT a `reason` string. There is no "original mailbox
 * link" URL field in this contract (`gmailAttachmentId` is an opaque Gmail
 * API id, not a URL), so none is guessed/rendered — see AttachmentRow in
 * support-ticket-detail-page.tsx.
 */
import { KnownAuthError, KnownError } from "@/api/client";
import { appEnv } from "@/constants/app-env";
import { env } from "@/constants/env";
import { supabase } from "@/lib/supabase";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const API_PREFIX = "/api/v1";

export type TicketStatus = "open" | "waiting" | "resolved";
export type TicketSource = "web" | "email" | "plain";
export type MessageKind = "customer" | "reply" | "note";
export type DeliveryStatus = "not_applicable" | "pending" | "sending" | "sent" | "failed" | "unknown";

export interface SupportAttachment {
  id: string;
  filename: string;
  contentType: string;
  size: number;
  /** When false, the original bytes are gone (oversized-at-ingestion or never stored) — must render as a plain, non-downloadable row, never a dead/fake link/button. */
  available: boolean;
  /** Opaque Gmail API attachment id; not a URL, not rendered. */
  gmailAttachmentId?: string;
}

export interface SupportTicket {
  id: string;
  reference: string;
  name: string;
  email: string;
  subject: string;
  status: TicketStatus;
  source: TicketSource;
  createdAt: string;
  updatedAt: string;
}

export interface SupportMessage {
  id: string;
  ticketId: string;
  kind: MessageKind;
  body: string;
  authorName: string;
  createdAt: string;
  deliveryStatus: DeliveryStatus;
  deliveryError?: string;
  attachments?: SupportAttachment[];
}

// ---- Public (unauthenticated) ----

export interface SubmitSupportTicketPayload {
  name: string;
  email: string;
  subject: string;
  body: string;
  requestId: string;
  /** Honeypot field. Must stay empty; never rendered visibly to real users. */
  website?: string;
}

export interface SubmitSupportTicketResponse {
  id: string;
  reference: string;
  status: TicketStatus;
  createdAt: string;
}

// ---- Staff (authenticated, /admin/support) ----

export interface ListAdminSupportTicketsParams {
  status?: TicketStatus;
  q?: string;
  page?: number;
}

export interface ListAdminSupportTicketsResponse {
  items: SupportTicket[];
  total: number;
  page: number;
  pageSize: number;
}

export interface GetAdminSupportTicketResponse {
  ticket: SupportTicket;
  messages: SupportMessage[];
}

export interface PostAdminSupportMessagePayload {
  body: string;
  kind: Extract<MessageKind, "reply" | "note">;
  requestId: string;
}

export interface UpdateAdminSupportTicketPayload {
  status: TicketStatus;
}

// ---- Follow-up worklist (GET /admin/support/followups) ----

export type SupportFollowupGroup = "payout_blocked" | "trolley_returned" | "individual";

export interface SupportFollowupItem {
  ticket: SupportTicket;
  group: SupportFollowupGroup;
  order: number | null;
  summary: string | null;
  /** When staff checked "조치 완료" (internal action done); null when not done. */
  resolvedAt: string | null;
  resolvedBy: string | null;
  /** Derived server-side: at least one staff reply with deliveryStatus "sent". */
  replied: boolean;
  lastReplyAt: string | null;
  /** Full thread, same shape as ticket detail (importer metadata / Plain digest notes excluded). */
  messages: SupportMessage[];
}

export interface ListAdminSupportFollowupsResponse {
  items: SupportFollowupItem[];
  summary: { total: number; resolved: number; replied: number; done: number };
}

export interface UpdateAdminSupportFollowupPayload {
  resolved: boolean;
}

export interface UpdateAdminSupportFollowupResponse {
  ticketId: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
}

// ---- fetch wrapper ----

function buildUrl(path: string, searchParams?: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams();
  if (searchParams != null) {
    for (const [key, value] of Object.entries(searchParams)) {
      if (value != null && value !== "") {
        query.set(key, String(value));
      }
    }
  }
  const search = query.toString();
  const fullPath = `${API_PREFIX}${path}${search.length > 0 ? `?${search}` : ""}`;

  // NOTE: Mirrors "@/api/client" getUrl()'s dev-proxy behavior: vite.config.ts
  // proxies "/api" to the local API server, so a relative path is enough in
  // development, avoiding CORS.
  if (appEnv.isDevelopment) {
    return fullPath;
  }
  return `${env.API_BASE}${fullPath}`;
}

async function getAuthHeaders(): Promise<{ headers: HeadersInit; hasSession: boolean }> {
  const accessToken = (await supabase.auth.getSession()).data.session?.access_token;
  const headers: HeadersInit = { "Content-Type": "application/json" };
  if (accessToken != null) {
    return { headers: { ...headers, Authorization: `Bearer ${accessToken}` }, hasSession: true };
  }
  return { headers, hasSession: false };
}

async function parseJsonSafely(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function extractErrorMessage(payload: unknown, fallback: string): string {
  if (payload != null && typeof payload === "object" && "message" in payload) {
    const message = (payload as { message?: unknown }).message;
    if (typeof message === "string") {
      return message;
    }
    if (Array.isArray(message) && message.every((part) => typeof part === "string")) {
      return message.join(", ");
    }
  }
  return fallback;
}

async function request<T>(
  path: string,
  options: {
    method: "GET" | "POST" | "PATCH";
    body?: unknown;
    searchParams?: Record<string, string | number | undefined>;
    signal?: AbortSignal;
  },
): Promise<T> {
  const url = buildUrl(path, options.searchParams);
  const { headers, hasSession } = await getAuthHeaders();

  const response = await fetch(url, {
    method: options.method,
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
    credentials: "include",
  });

  const payload = await parseJsonSafely(response);

  if (response.status === 401) {
    // The backend's staff guard (see apps/server/src/modules/support/support.guard.ts,
    // read while implementing this — not part of this task's own edits) also throws a
    // plain 401 for "logged in, but not an @backpac.app/@contentstech.com staff account",
    // not just for "no session at all". Only treat this as an auth-boundary redirect case
    // when we genuinely sent no bearer token; otherwise the signed-in-but-not-staff visitor
    // would bounce to sign-in and loop forever. In the latter case, surface it as a normal
    // KnownError so the page can render an honest "not authorized as staff" message instead.
    if (!hasSession) {
      throw new KnownAuthError("Unauthorized", window.location.href);
    }
    throw new KnownError(extractErrorMessage(payload, "Not authorized"), response.status);
  }

  if (!response.ok) {
    throw new KnownError(extractErrorMessage(payload, response.statusText), response.status);
  }

  return payload as T;
}

// ---- Public API ----

export async function submitSupportTicket(
  payload: SubmitSupportTicketPayload,
  signal?: AbortSignal,
): Promise<SubmitSupportTicketResponse> {
  return request<SubmitSupportTicketResponse>("/support/tickets", { method: "POST", body: payload, signal });
}

export function useSubmitSupportTicket() {
  return useMutation({
    mutationFn: (payload: SubmitSupportTicketPayload) => submitSupportTicket(payload),
  });
}

// ---- Admin API ----

export async function listAdminSupportTickets(
  params: ListAdminSupportTicketsParams,
  signal?: AbortSignal,
): Promise<ListAdminSupportTicketsResponse> {
  return request<ListAdminSupportTicketsResponse>("/admin/support/tickets", {
    method: "GET",
    searchParams: { status: params.status, q: params.q, page: params.page },
    signal,
  });
}

export function adminSupportTicketsQueryKey(params: ListAdminSupportTicketsParams) {
  return ["admin-support-tickets", params.status ?? null, params.q ?? "", params.page ?? 1] as const;
}

export function useAdminSupportTickets(params: ListAdminSupportTicketsParams) {
  return useQuery({
    queryKey: adminSupportTicketsQueryKey(params),
    queryFn: ({ signal }) => listAdminSupportTickets(params, signal),
  });
}

export async function getAdminSupportTicket(id: string, signal?: AbortSignal): Promise<GetAdminSupportTicketResponse> {
  return request<GetAdminSupportTicketResponse>(`/admin/support/tickets/${id}`, { method: "GET", signal });
}

export function adminSupportTicketQueryKey(id: string) {
  return ["admin-support-ticket", id] as const;
}

export function useAdminSupportTicket(id: string) {
  return useQuery({
    queryKey: adminSupportTicketQueryKey(id),
    queryFn: ({ signal }) => getAdminSupportTicket(id, signal),
    enabled: id.length > 0,
  });
}

export async function postAdminSupportMessage(
  ticketId: string,
  payload: PostAdminSupportMessagePayload,
  signal?: AbortSignal,
): Promise<SupportMessage> {
  return request<SupportMessage>(`/admin/support/tickets/${ticketId}/messages`, {
    method: "POST",
    body: payload,
    signal,
  });
}

export function useAdminSupportMessageMutation(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PostAdminSupportMessagePayload) => postAdminSupportMessage(ticketId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSupportTicketQueryKey(ticketId) });
      queryClient.invalidateQueries({ queryKey: adminSupportFollowupsQueryKey() });
    },
  });
}

export async function updateAdminSupportTicketStatus(
  ticketId: string,
  payload: UpdateAdminSupportTicketPayload,
  signal?: AbortSignal,
): Promise<SupportTicket> {
  return request<SupportTicket>(`/admin/support/tickets/${ticketId}`, { method: "PATCH", body: payload, signal });
}

export function useAdminSupportTicketStatusMutation(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateAdminSupportTicketPayload) => updateAdminSupportTicketStatus(ticketId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSupportTicketQueryKey(ticketId) });
      queryClient.invalidateQueries({ queryKey: ["admin-support-tickets"] });
      queryClient.invalidateQueries({ queryKey: adminSupportFollowupsQueryKey() });
    },
  });
}

export async function listAdminSupportFollowups(signal?: AbortSignal): Promise<ListAdminSupportFollowupsResponse> {
  return request<ListAdminSupportFollowupsResponse>("/admin/support/followups", { method: "GET", signal });
}

export function adminSupportFollowupsQueryKey() {
  return ["admin-support-followups"] as const;
}

export function useAdminSupportFollowups() {
  return useQuery({
    queryKey: adminSupportFollowupsQueryKey(),
    queryFn: ({ signal }) => listAdminSupportFollowups(signal),
  });
}

export async function updateAdminSupportFollowup(
  ticketId: string,
  payload: UpdateAdminSupportFollowupPayload,
  signal?: AbortSignal,
): Promise<UpdateAdminSupportFollowupResponse> {
  return request<UpdateAdminSupportFollowupResponse>(`/admin/support/tickets/${ticketId}/followup`, {
    method: "PATCH",
    body: payload,
    signal,
  });
}

export function useAdminSupportFollowupMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, resolved }: { ticketId: string; resolved: boolean }) =>
      updateAdminSupportFollowup(ticketId, { resolved }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSupportFollowupsQueryKey() });
    },
  });
}

export async function retryAdminSupportMessage(messageId: string, signal?: AbortSignal): Promise<SupportMessage> {
  return request<SupportMessage>(`/admin/support/messages/${messageId}/retry`, { method: "POST", signal });
}

export function useAdminSupportMessageRetryMutation(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => retryAdminSupportMessage(messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminSupportTicketQueryKey(ticketId) });
    },
  });
}

/**
 * Downloads an attachment's bytes as a `Blob`, authenticated with the same
 * Supabase bearer token as every other admin call. Deliberately NOT a plain
 * `<a href="...">` to the API URL: that would either leak the bearer token
 * in a URL (if appended as a query param) or silently fail to authenticate
 * (a bare anchor click can't set an Authorization header). Callers should
 * turn the resulting Blob into a same-origin `blob:` object URL for the
 * actual download trigger (see `routes/admin/support/lib/download.ts`),
 * which never exposes the token or the raw API URL to the DOM.
 */
export async function fetchAdminSupportAttachmentBlob(attachmentId: string, signal?: AbortSignal): Promise<Blob> {
  const url = buildUrl(`/admin/support/attachments/${attachmentId}`);
  const { headers, hasSession } = await getAuthHeaders();

  const response = await fetch(url, {
    method: "GET",
    headers,
    signal,
    credentials: "include",
  });

  if (!response.ok) {
    const payload = await parseJsonSafely(response);
    if (response.status === 401 && !hasSession) {
      throw new KnownAuthError("Unauthorized", window.location.href);
    }
    throw new KnownError(extractErrorMessage(payload, response.statusText), response.status);
  }

  return response.blob();
}
